/**
 * The language the admin is *reading in* — not the one their browser happens to be set to.
 *
 * `toLocaleString()` with no locale follows the browser. A waiter at a French restaurant
 * whose Chrome is in English therefore read "Ouverte à : 9/18/2026, 2:29:51 PM" on a
 * French screen, and picked sessions from a list labelled "08/10" that actually meant
 * 10 August. Month and day silently swap places, which is worse than ugly: it makes
 * someone choose the wrong day's till.
 *
 * Every date and number shown in the admin resolves its locale here instead.
 */

const LOCALE_BY_LANGUAGE = {
  fr: 'fr-FR', en: 'en-GB', de: 'de-DE', es: 'es-ES',
  it: 'it-IT', ru: 'ru-RU', ar: 'ar-TN',
};

export const DEFAULT_LOCALE = 'fr-FR';

/** The BCP-47 tag behind the admin's current language. */
export const activeLocale = () => {
  if (typeof window === 'undefined') return DEFAULT_LOCALE;
  let stored = null;
  try {
    stored = localStorage.getItem('i18nextLng');
  } catch {
    // private browsing or blocked storage: the default still reads correctly
  }
  const lng = (stored || 'fr').slice(0, 2).toLowerCase();
  return LOCALE_BY_LANGUAGE[lng] || DEFAULT_LOCALE;
};

/**
 * The locale for NUMBERS and AMOUNTS (QA 21/10 point 5). In Arabic, 'ar-TN' groups thousands with a point
 * (« 4.406 DT » = 4 406 DT, which a Tunisian reads as 4 dinars 406 millimes) and other screens fell back to
 * « 20,783.54 » or « 20.22 »: Arabic now writes its figures exactly like French — Latin digits, a space
 * between thousands, a comma before the decimals (« 4 406 DT », « 20 783,54 DT », « 20,22 DT »).
 * Every other language keeps its own convention. Dates and words keep `activeLocale()`.
 */
export const numberLocale = (locale) => {
  const loc = locale || activeLocale();
  return /^ar(-|$)/i.test(loc) ? DEFAULT_LOCALE : loc;
};

const isArabic = () => activeLocale().startsWith('ar');

/** Latin « 6h 19m » in every language (owner 21/10: no Arabic hour letters); in Arabic it is isolated
 *  left-to-right (LRI … PDI) so the right-to-left page never lays it out backwards (« m 19 h 6 »). */
export const ltrIsolate = (text) => (isArabic() ? `\u2066${text}\u2069` : text);

/** « 14h ». */
export const atHour = (h) => ltrIsolate(`${h}h`);

/** An elapsed time « 1d 4h 21m ». */
export const elapsedHM = (hours, minutes, days = 0) =>
  ltrIsolate(`${days > 0 ? `${days}d ` : ''}${hours}h ${minutes}m`);

// Dates and times: one implementation, in the RESTAURANT's zone and the reader's language.
export { formatDate, formatDateTime, formatTime } from './datetime';
